import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@cartcloser/ui';
import { authClient } from '../auth.js';

export interface NavItem {
  label: string;
  to: string;
  /** Match this route exactly (so a section root isn't "active" on children). */
  exact?: boolean;
}

/** A group of nav links, optionally under a heading. No heading = top-level. */
export interface NavSection {
  heading?: string;
  items: NavItem[];
}

function NavLinks({ sections, onNavigate }: { sections: NavSection[]; onNavigate?: () => void }) {
  return (
    <nav className="space-y-1">
      {sections.map((section, i) => (
        <div key={section.heading ?? `top-${i}`} className={i > 0 ? 'pt-3' : undefined}>
          {section.heading ? (
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {section.heading}
            </p>
          ) : null}
          {section.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.exact ?? false }}
              onClick={onNavigate}
              className="block rounded-md px-3 py-2 text-sm text-foreground hover:bg-accent [&.active]:bg-primary [&.active]:text-primary-foreground"
            >
              {item.label}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

function SidebarBody({
  brandSub,
  topLink,
  nav,
  onNavigate,
}: {
  brandSub: string;
  topLink?: NavItem;
  nav: NavSection[];
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="mb-6 px-2">
        <p className="text-xs font-semibold tracking-wide text-primary">CartCloser</p>
        <p className="truncate text-sm font-medium">{brandSub}</p>
      </div>
      {topLink ? (
        <Link
          to={topLink.to}
          onClick={onNavigate}
          className="mb-3 block rounded-md px-3 py-2 text-sm text-primary hover:bg-accent"
        >
          {topLink.label}
        </Link>
      ) : null}
      <NavLinks sections={nav} onNavigate={onNavigate} />
    </>
  );
}

/**
 * Shared dashboard chrome: a vertical left sidebar on desktop, a hamburger +
 * slide-in drawer below md, and a top header. Used by both the merchant admin
 * section and the platform (super) section so they navigate identically.
 */
export function AppShell({
  brandSub,
  nav,
  topLink,
  email,
  headerLabel,
  headerExtra,
  children,
}: {
  brandSub: string;
  nav: NavSection[];
  /** Optional link above the nav, e.g. "← Dashboard" out of the platform area. */
  topLink?: NavItem;
  email: string;
  /** Muted label on the left of the header, e.g. "/my-store". */
  headerLabel?: string;
  /** Extra header content left of email, e.g. the merchant's "Platform" link. */
  headerExtra?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 border-r border-border bg-secondary/40 p-4 md:block">
        <SidebarBody brandSub={brandSub} topLink={topLink} nav={nav} />
      </aside>

      {menuOpen ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <button
            aria-label="Close menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-64 max-w-[80vw] overflow-y-auto border-r border-border bg-background p-4 shadow-brand">
            <SidebarBody
              brandSub={brandSub}
              topLink={topLink}
              nav={nav}
              onNavigate={() => setMenuOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <button
              aria-label="Open menu"
              className="-ml-1 rounded-md p-2 text-foreground hover:bg-accent md:hidden"
              onClick={() => setMenuOpen(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path
                  d="M3 5h14M3 10h14M3 15h14"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {headerLabel ? (
              <span className="truncate text-sm text-muted-foreground">{headerLabel}</span>
            ) : null}
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            {headerExtra}
            <span className="hidden min-w-0 truncate text-sm text-muted-foreground sm:inline">
              {email}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="shrink-0"
              onClick={async () => {
                await authClient.signOut();
                queryClient.removeQueries({ queryKey: ['me'] });
                await navigate({ to: '/signin' });
              }}
            >
              Sign out
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
